import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FieldRow } from "@/components/feature/FieldRow";
import { Combobox } from "@/components/ui/combobox";
import { getAppointmentOrganizer } from "../helpers/organizerHelpers";

// Stabiler Rückfall für noch nicht geladene Listen — ein frisches `[]` pro
// Render würde die `useMemo`/`useEffect`-Abhängigkeiten bei jedem Durchlauf
// als geändert gelten lassen.
const EMPTY_LIST = [];

const emailKey = (email) => String(email || "").trim().toLowerCase();

const hostLabel = (host) =>
  host.first_name && host.last_name ? `${host.first_name} ${host.last_name}` : host.display_name || host.email;

const Host = () => {
  const [selectedHost, setSelectedHost] = useState("");
  // Organisator des Outlook-Termins, sobald Office ihn geliefert hat. Er ist
  // nicht die Vorauswahl, steht aber zur Auswahl — in einem Kalender mit
  // Bearbeitungsrecht ist er die Person, der der Kalender gehört.
  // `resolved` trennt "noch nicht gefragt" von "es gibt keinen".
  const [organizer, setOrganizer] = useState(null);
  const [organizerResolved, setOrganizerResolved] = useState(false);

  // Rückfall bewusst ausserhalb des Selektors: `|| []` darin liefert bei jeder
  // Store-Meldung ein neues Array und damit einen endlosen Renderlauf, solange
  // die Gastgeber noch nicht geladen sind.
  const hosts = useSelector((state) => state.company.data.hosts) || EMPTY_LIST;
  const appointmentHostEmail = useSelector((state) => state.getAppointment.data.host_email);
  const appointmentHostName = useSelector((state) => state.getAppointment.data.host_name);
  const userData = useSelector((state) => state.user.data.user);
  const visitors = useSelector((state) => state.visitors.data) || EMPTY_LIST;

  const dispatch = useDispatch();

  // Der gespeicherte Gastgeber wird genau einmal übernommen — sonst würde er
  // jede spätere Änderung sofort wieder überschreiben.
  const appliedSavedHost = useRef(null);
  // Zuletzt an den Termin gemeldete Gastgeber-Angaben.
  const lastDispatched = useRef(null);

  useEffect(() => {
    let active = true;
    getAppointmentOrganizer()
      .catch(() => null)
      .then((entry) => {
        if (!active) return;
        setOrganizer(entry);
        setOrganizerResolved(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const augmentedHosts = useMemo(() => {
    const allHosts = [...hosts];
    const add = (entry) => {
      if (!entry?.email) return;
      if (allHosts.some((host) => emailKey(host.email) === emailKey(entry.email))) return;
      allHosts.push(entry);
    };
    // Angemeldete Person, Organisator und der bereits gespeicherte Gastgeber
    // stehen zur Auswahl, auch wenn sie am Standort nicht als Gastgeber
    // hinterlegt sind.
    if (userData) {
      add({
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        azure_id: userData.azure_id,
      });
    }
    if (organizer) {
      add({ email: organizer.email, display_name: organizer.name, azure_id: null });
    }
    if (appointmentHostEmail) {
      add({ email: appointmentHostEmail, display_name: appointmentHostName, azure_id: null });
    }
    return allHosts;
  }, [hosts, userData, organizer, appointmentHostEmail, appointmentHostName]);

  useEffect(() => {
    if (augmentedHosts.length === 0) return;

    const find = (email) => augmentedHosts.find((host) => emailKey(host.email) === emailKey(email));

    // Gespeicherter Termin: der abgelegte Gastgeber ist die Vorgabe.
    if (appointmentHostEmail && appliedSavedHost.current !== emailKey(appointmentHostEmail)) {
      const saved = find(appointmentHostEmail);
      if (!saved) return;
      appliedSavedHost.current = emailKey(appointmentHostEmail);
      setSelectedHost(saved.email);
      return;
    }
    if (selectedHost) return;
    // Neuer Termin: vorausgewählt ist, wer den Termin erfasst.
    const own = find(userData?.email);
    if (own) {
      setSelectedHost(own.email);
      return;
    }
    // Ohne Angaben zur angemeldeten Person tritt der Organisator an ihre
    // Stelle — darauf lohnt sich das Warten. Ist auch er unbekannt, bleibt das
    // Feld leer: ein beliebiger Standort-Gastgeber wäre eine Falschangabe.
    if (!organizerResolved) return;
    const organizerEntry = find(organizer?.email);
    if (organizerEntry) setSelectedHost(organizerEntry.email);
  }, [appointmentHostEmail, augmentedHosts, selectedHost, organizer, organizerResolved, userData]);

  useEffect(() => {
    if (!selectedHost) return;
    const hostData = augmentedHosts.find((host) => emailKey(host.email) === emailKey(selectedHost));
    if (!hostData) return;
    const payload = {
      host_teams_id: hostData.azure_id,
      host_email: hostData.email,
      host_name: hostLabel(hostData),
      // Wer den Termin im Add-in erfasst, bleibt der Ersteller — auch wenn der
      // Gastgeber jemand anderes ist.
      creator_email: userData?.email,
    };
    // Unveränderte Angaben nicht erneut melden: jeder Dispatch erzeugt einen
    // neuen Termin-Zustand und damit einen weiteren Renderlauf.
    const signature = JSON.stringify(payload);
    if (lastDispatched.current === signature) return;
    lastDispatched.current = signature;
    dispatch({ type: "SET_APPOINTMENT_DATA", payload });
  }, [selectedHost, augmentedHosts, userData?.email, dispatch]);

  const dropdownOptions = useMemo(() => {
    const attendees = new Set(visitors.map((visitor) => emailKey(visitor.email)));
    const organizerKey = emailKey(organizer?.email);
    const ownKey = emailKey(userData?.email);
    const savedKey = emailKey(appointmentHostEmail);

    // Zur Auswahl stehen nur Gastgeber mit Bezug zum Termin — die vollständige
    // Standortliste wäre zu unübersichtlich. Der Organisator gehört dazu,
    // obwohl er nicht in der Teilnehmerliste steht: in einem Kalender mit
    // Bearbeitungsrecht ist er die Person, für die der Termin läuft. Die Zahl
    // ist zugleich die Reihenfolge, `null` heisst "steht nicht zur Auswahl".
    const rank = (host) => {
      const key = emailKey(host.email);
      if (ownKey && key === ownKey) return 0;
      if (organizerKey && key === organizerKey) return 1;
      if (attendees.has(key)) return 2;
      if (savedKey && key === savedKey) return 3;
      return null;
    };

    return augmentedHosts
      .map((host) => ({ host, rank: rank(host) }))
      .filter((entry) => entry.rank !== null)
      .sort((a, b) => a.rank - b.rank || hostLabel(a.host).localeCompare(hostLabel(b.host), "de"))
      .map(({ host }) => ({
        label: hostLabel(host),
        value: host.email,
      }));
  }, [augmentedHosts, visitors, userData, organizer, appointmentHostEmail]);

  return (
    <FieldRow label="Gastgeber">
      <Combobox
        options={dropdownOptions}
        value={selectedHost}
        onChange={(value) => setSelectedHost(value)}
        placeholder="Gastgeber wählen…"
      />
    </FieldRow>
  );
};

export default Host;
